import { Badge } from "../components/Badge";
import { Button } from "../components/Button";

function ShieldIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-6 w-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 2 4 6v6c0 5.25 3.8 10.1 8 11.25C16.2 22.1 20 17.25 20 12V6l-8-4Z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function AgentIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-6 w-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M5 20c0-4.5 3.1-7.5 7-7.5s7 3 7 7.5" />
      <path d="M17 6.5 19.5 4M18.5 4H16v2.5" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-6 w-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <ellipse cx="12" cy="12" rx="4" ry="9" />
      <path d="M3 12h18" />
    </svg>
  );
}

const VALUES = [
  {
    icon: <ShieldIcon />,
    title: "You Own Your Data",
    desc: "Health data belongs to the person it describes. Full stop. We build systems where you hold the keys.",
    tone: "text-kin-green-dark",
    bg: "bg-kin-green/10",
  },
  {
    icon: <AgentIcon />,
    title: "AI as Your Advocate",
    desc: "Intelligent agents that work for you — analyzing, correlating, and surfacing insights across your entire health timeline.",
    tone: "text-kin-lavender",
    bg: "bg-kin-lavender/10",
  },
  {
    icon: <GlobeIcon />,
    title: "Open Ecosystem",
    desc: "An open marketplace where health experts, researchers, and developers publish guides that anyone can install and run.",
    tone: "text-kin-gold-dark",
    bg: "bg-kin-gold/10",
  },
];

const TEAM_LOGOS = [
  { company: "Google", src: "/logos/google.png" },
  { company: "Babylon Health", src: "/logos/babylon-health.png" },
  { company: "Meta", src: "/logos/meta.png" },
  { company: "Microsoft", src: "/logos/microsoft.png" },
];

export function CompanyPage() {
  return (
    <div className="px-6 pb-16 pt-10 sm:px-8 sm:pb-20 sm:pt-14">
      {/* Hero */}
      <section className="mx-auto max-w-6xl">
        <Badge variant="lavender">About Kin</Badge>
        <h1 className="mt-4 max-w-4xl font-serif text-5xl font-bold leading-[1.05] tracking-tight text-kin-black sm:text-6xl md:text-7xl">
          We&rsquo;re building the future of personal health intelligence.
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-kin-body/90">
          Kin is a startup on a mission to give every person true ownership of their health data —
          and the AI tools to make sense of it.
        </p>
      </section>

      {/* Photo */}
      <section className="mx-auto mt-10 max-w-6xl">
        <div className="overflow-hidden rounded-[28px] shadow-[0_24px_48px_-24px_rgba(0,0,0,0.18)]">
          <img
            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&q=80&auto=format&fit=crop"
            alt="Team collaborating"
            className="h-72 w-full object-cover sm:h-96"
          />
        </div>
      </section>

      {/* Team background */}
      <section className="mx-auto mt-16 max-w-6xl">
        <div className="kin-surface rounded-3xl p-8 sm:p-10">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">Team</p>
          <h2 className="mt-3 font-serif text-3xl font-bold text-kin-black sm:text-4xl">
            Built by people who&rsquo;ve shipped at scale.
          </h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-kin-body/90">
            Our team brings deep experience from some of the world&rsquo;s leading technology and
            health companies.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-6">
            {TEAM_LOGOS.map((t) => (
              <img
                key={t.company}
                src={t.src}
                alt={t.company}
                className="h-6 opacity-50 grayscale transition hover:opacity-80"
                onError={(e) => {
                  // hide missing logos gracefully
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="mx-auto mt-16 max-w-6xl">
        <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
          What We Believe
        </p>
        <h2 className="mt-3 font-serif text-3xl font-bold text-kin-black sm:text-4xl">
          Principles that guide everything we build.
        </h2>
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {VALUES.map((v) => (
            <article key={v.title} className="kin-surface rounded-3xl p-6">
              <span
                className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl ${v.bg} ${v.tone}`}
              >
                {v.icon}
              </span>
              <h3 className="mt-4 text-lg font-semibold text-kin-black">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-kin-body/90">{v.desc}</p>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto mt-16 max-w-6xl">
        <article className="kin-surface-strong relative overflow-hidden rounded-[34px] p-7 sm:p-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(135deg, rgba(52,199,89,0.14) 0%, rgba(255,255,255,0.88) 50%, rgba(175,82,222,0.14) 100%)",
            }}
          />
          <div className="relative grid gap-6 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
                Join Us
              </p>
              <h2 className="mt-3 font-serif text-4xl font-bold leading-tight text-kin-black sm:text-5xl">
                Interested in what we&rsquo;re building?
              </h2>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-kin-body/92">
                We&rsquo;re hiring engineers, designers, and health domain experts who share our
                vision.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:flex-col lg:items-end">
              <a href="mailto:hello@kinhealth.ai?subject=Kin%20Team%20Inquiry">
                <Button size="large">Get in Touch</Button>
              </a>
            </div>
          </div>
        </article>
      </section>
    </div>
  );
}
